import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline'
import clsx from 'clsx'
import { groupBy, keyBy, mapValues, sortBy, uniq } from 'lodash'
import Link from 'next/link'

import { Bet } from 'common/bet'
import { calculateUserMetricsWithoutLoans } from 'common/calculate-metrics'
import { Contract, contractPath } from 'common/contract'
import { ContractMetric } from 'common/contract-metric'
import {
  excludeSelfTrades,
  filterBetsForLeagueScoring,
  getApproximateSeasonDates,
} from 'common/leagues'
import { formatMoney } from 'common/util/format'
import { usePublicContracts } from 'web/hooks/use-contract'
import { Col } from '../layout/col'
import { Modal, MODAL_CLASS } from '../layout/modal'
import { Row } from '../layout/row'
import { ProfitBadge } from '../profit-badge'
import { LoadingIndicator } from '../widgets/loading-indicator'
import { UserAvatarAndBadge } from '../widgets/user-link'

import { useBetsOnce } from 'client-common/hooks/use-bets'
import { DisplayUser } from 'common/api/user-types'
import { TRADE_TERM } from 'common/envs/constants'
import { capitalize } from 'lodash'
import { useState } from 'react'
import { ContractBetsTable } from 'web/components/bet/contract-bets-table'
import { useAPIGetter } from 'web/hooks/use-api-getter'
import { api } from 'web/lib/api/api'
import ShortToggle from '../widgets/short-toggle'

export const ManaEarnedBreakdown = (props: {
  user: DisplayUser
  season: number
  showDialog: boolean
  setShowDialog: (show: boolean) => void
  mana_earned: number
  mana_earned_breakdown: { [key: string]: number }
}) => {
  const { user, season, showDialog, setShowDialog, mana_earned_breakdown } =
    props

  // const breakdown = {
  //   PROFIT: mana_earned_breakdown.profit,
  //   ...mana_earned_breakdown,
  //   MARKET_BOOST_REDEEM:
  //     (mana_earned_breakdown.MARKET_BOOST_REDEEM ?? 0) +
  //     (mana_earned_breakdown.AD_REDEEM ?? 0),
  // } as { [key: string]: number }

  // Get the authoritative season boundaries from the database
  const { data: seasonInfo } = useAPIGetter('get-season-info', { season })
  // Use proper start time from API, fall back to approximate if not loaded yet
  const { start: approxStart, approxEnd } = getApproximateSeasonDates(season)
  const start = seasonInfo?.startTime
    ? new Date(seasonInfo.startTime)
    : approxStart

  // For end time: use API value if available (will be null for active seasons),
  // otherwise fall back to approximate end for display
  const end = seasonInfo?.endTime ? new Date(seasonInfo.endTime) : approxEnd
  const loadingBets = useBetsOnce((params) => api('bets', params), {
    userId: user.id,
    afterTime: start.getTime(),
    beforeTime: end.getTime(),
    order: 'desc',
  })
  const bets = loadingBets ?? []

  const contractIds = loadingBets
    ? uniq(loadingBets.map((b) => b.contractId))
    : undefined
  const contracts = usePublicContracts(contractIds)?.filter(
    (c) => c.isRanked !== false && c.token === 'MANA'
  )

  const contractsById = keyBy(contracts, 'id')

  const betsByContract = groupBy(bets, 'contractId')
  const metricsByContract =
    contracts &&
    mapValues(betsByContract, (bets, contractId) => {
      const contract = contractsById[contractId]
      if (!contract) return undefined

      // Filter bets: if it's user's own market, only count bets placed 1+ hour after creation
      // Adjust bets to exclude portions that filled against user's own limit orders
      const nonSelfTradeBets = excludeSelfTrades(bets, user.id)

      // Filter bets: if it's user's own market, only count bets placed 1+ hour after creation
      const relevantBets = filterBetsForLeagueScoring(
        nonSelfTradeBets,
        contract,
        user.id
      )

      return relevantBets.length > 0
        ? calculateUserMetricsWithoutLoans(
            contract,
            relevantBets,
            user.id
          ).find((cm) => !cm.answerId)
        : undefined
    })

  const [showHighestFirst, setShowHighestFirst] = useState(true)

  const contractsSorted =
    contracts &&
    metricsByContract &&
    sortBy(contracts, (contract) => metricsByContract[contract.id]?.profit ?? 0)

  const contractsSortedByProfit = showHighestFirst
    ? contractsSorted?.reverse()
    : contractsSorted

  return (
    <Modal
      className={clsx(MODAL_CLASS, '')}
      open={showDialog}
      setOpen={(open) => setShowDialog(open)}
    >
      <Col>
        <Row className="mb-2 items-center gap-4">
          <UserAvatarAndBadge user={user} />
        </Row>
        <Row className="items-baseline justify-between">
          <span className="text-primary-700 mb-2 text-lg">
            Profit on trades placed this season:
          </span>
          {formatMoney(mana_earned_breakdown?.profit ?? 0)}
        </Row>
        <Row className="items-baseline justify-between">
          <span className="text-primary-700 mb-2 text-lg">
            Mana earned from Unique Trader Bonuses:
          </span>
          {formatMoney(mana_earned_breakdown?.UNIQUE_BETTOR_BONUS ?? 0)}
        </Row>
        <span className="text-ink-600 text-sm">
          Only counts profit on trades placed on/after{' '}
          {start.toLocaleDateString()}.
        </span>
        {/* <Table className="text-base">
           <thead className={clsx('text-ink-600 text-left font-semibold')}>
            <tr>
              <th className={clsx('px-2 pb-1')}>Category</th>
              <th className={clsx('px-2 pb-1 text-right')}>Amount</th>
            </tr>
          </thead>
          <tbody>
             {Object.keys(MANA_EARNED_CATEGORY_LABELS).map((category) =>
              (category === 'CREATOR_FEE' ||
                category === 'UNIQUE_BETTOR_BONUS') &&
              !breakdown[category] ? null : (
                <tr key={category}>
                  <td className={clsx('pl-2')}>
                    {MANA_EARNED_CATEGORY_LABELS[category]}
                  </td>
                  <td className={clsx('pr-2 text-right')}>
                    {formatMoney(breakdown[category] ?? 0)}
                  </td>
                </tr>
              )
            )}
            <tr className="font-semibold">
              <td className={clsx('pl-2')}>Total</td>
              <td className={clsx('pr-2 text-right')}>
                {formatMoney(mana_earned)}
              </td>
            </tr>
          </tbody>
        </Table> */}

        {contracts && contracts.length > 0 && (
          <Col>
            <Row className="my-4 gap-2">
              <ShortToggle on={showHighestFirst} setOn={setShowHighestFirst} />{' '}
              Highest first
            </Row>
          </Col>
        )}

        {contracts === undefined && (
          <div className="h-[500px]">
            <LoadingIndicator className="mt-6" />
          </div>
        )}
        <Col className="gap-6">
          {contracts &&
            contractsSortedByProfit &&
            metricsByContract &&
            contractsSortedByProfit.map((contract) => {
              const bets = betsByContract[contract.id]
              const metrics = metricsByContract[contract.id]
              if (!bets || !metrics) return null

              // Filter bets: if it's user's own market, only show bets placed 1+ hour after creation
              // Adjust bets to exclude portions that filled against user's own limit orders
              const nonSelfTradeBets = excludeSelfTrades(bets, user.id)

              // Filter bets: if it's user's own market, only show bets placed 1+ hour after creation
              const relevantBets = filterBetsForLeagueScoring(
                nonSelfTradeBets,
                contract,
                user.id
              )

              if (relevantBets.length === 0) return null

              return (
                <ContractBetsEntry
                  key={contract.id}
                  contract={contract}
                  bets={relevantBets}
                  metrics={metrics}
                />
              )
            })}
        </Col>
      </Col>
    </Modal>
  )
}

const ContractBetsEntry = (props: {
  contract: Contract
  bets: Bet[]
  metrics: ContractMetric
}) => {
  const { bets, metrics, contract } = props
  const { profit, profitPercent } = metrics

  const showExpander = bets.length > 2
  const [expanded, setExpanded] = useState(false)

  return (
    <Col>
      <Row className="gap-2">
        <Link
          href={contractPath(contract)}
          className="text-primary-700 hover:decoration-primary-400 flex-1 font-medium hover:underline hover:decoration-2"
          onClick={(e) => e.stopPropagation()}
        >
          {contract.question}
        </Link>

        <Col>
          <div className="whitespace-nowrap text-right text-lg">
            {formatMoney(profit)}
          </div>
          <ProfitBadge className="text-right" profitPercent={profitPercent} />
        </Col>
      </Row>

      {showExpander && (
        <Row
          className="cursor-pointer items-center gap-2 self-start"
          tabIndex={0}
          onClick={() => setExpanded(!expanded)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setExpanded(!expanded)
          }}
        >
          {expanded ? (
            <ChevronUpIcon className="h-5 w-5" />
          ) : (
            <ChevronDownIcon className="h-5 w-5" />
          )}
          {capitalize(TRADE_TERM)}s
        </Row>
      )}

      {(!showExpander || expanded) && (
        <ContractBetsTable
          contract={contract}
          bets={bets}
          isYourBets={false}
          contractMetric={metrics}
          hideRedemptionAndLoanMessages
        />
      )}
    </Col>
  )
}

// const MANA_EARNED_CATEGORY_LABELS = {
//   PROFIT: 'Profit',
//   UNIQUE_BETTOR_BONUS: 'Trader bonuses',
// } as { [key: string]: string }
